import React, { useCallback, useEffect, useState } from "react";
import { Eye, HardDrive, Pencil, Search } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Pagination from "../../../components/Pagination/Pagination";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Notification from "../../../components/Notification/Notification";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";
import ViewStorageModal from "../../../components/Storage/ViewStorageModal";
import EditStorageModal from "../../../components/Storage/EditStorageModal";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./AdminStorage.module.scss";

const formatGB = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return `${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value))} GB`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return `${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value))}%`;
};

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminStorage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [viewModal, setViewModal] = useState({ open: false, id: null });
  const [editModal, setEditModal] = useState({ open: false, id: null });

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchStorageList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) params.set("search", search);

      const res = await apiGet(
        "catalog",
        `/billing/storage/orgs?${params.toString()}`,
      );

      if (res?.status === "success") {
        const payload = res.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRows(list);
        setPagination(
          payload.pagination || {
            page,
            limit,
            total: list.length,
            totalPages: 1,
          },
        );
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load storage list",
          }),
        );
      }
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to load storage list"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchStorageList();
  }, [fetchStorageList]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleResetSearch = () => {
    setSearchInput("");
    setPage(1);
    setSearch("");
  };

  const tableData = rows.map((row) => {
    const percent = Math.max(0, Math.min(100, Number(row.percentUsed ?? 0)));
    const tone =
      percent >= 90 ? "danger" : percent >= 70 ? "warning" : "primary";
    const availableGb = Number(row.storageAvailableBytes ?? 0) / 1_000_000_000;
    return {
      ...row,
      percent,
      tone,
      availableGb,
      onView: () => setViewModal({ open: true, id: row.orgId }),
      onEdit: () => setEditModal({ open: true, id: row.orgId }),
    };
  });

  const columns = [
    { label: "Organization", key: "name" },
    { label: "Company", key: "companyName" },
    { label: "Used", key: "used" },
    { label: "Limit", key: "limit" },
    { label: "Usage", key: "usage" },
    { label: "Last Updated", key: "updatedAt" },
    { label: "Actions", key: "action" },
  ];

  return (
    <div className={styles["admin-storage__page"]}>
      {notification && (
        <div className={styles["admin-storage__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-storage__header"]}>
        <div className={styles["admin-storage__icon-wrapper"]}>
          <HardDrive size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-storage__title-group"]}>
          <h2 className={styles["admin-storage__title"]}>Storage Management</h2>
          <p className={styles["admin-storage__subtitle"]}>
            Monitor and adjust storage limits across all organizations.
          </p>
        </div>
      </div>

      {/* CARD */}
      <div className={styles["admin-storage__card"]}>
        {/* FILTER BAR */}
        <div className={styles["admin-storage__filter-bar"]}>
          <div className={styles["admin-storage__search-group"]}>
            <Input
              type="search"
              placeholder="Search organization"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" icon={Search} onClick={handleSearch} />
            {search && (
              <button
                type="button"
                className={styles["admin-storage__reset-btn"]}
                onClick={handleResetSearch}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["admin-storage__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={7} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={tableData}
              renderCell={(row, key) => {
                if (key === "used") {
                  return formatGB(row.storageUsedGb);
                }
                if (key === "limit") {
                  return formatGB(row.storageLimitGb);
                }
                if (key === "usage") {
                  return (
                    <div className={styles["admin-storage__usage-cell"]}>
                      <div
                        className={`${styles["admin-storage__progress"]} ${
                          styles[`admin-storage__progress--${row.tone}`]
                        }`}
                      >
                        <div
                          className={styles["admin-storage__progress-fill"]}
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                      <span className={styles["admin-storage__usage-text"]}>
                        {formatPercent(row.percent)}
                      </span>
                    </div>
                  );
                }
                if (key === "updatedAt") {
                  return formatDateTime(row.updatedAt);
                }
                if (key === "action") {
                  return (
                    <div className={styles["admin-storage__action-cell"]}>
                      <Eye
                        size={20}
                        className={styles["admin-storage__action-icon"]}
                        onClick={row.onView}
                        aria-label="View Storage"
                        role="button"
                        tabIndex={0}
                      />
                      {can("storage", "manage") && (
                        <Pencil
                          size={20}
                          className={styles["admin-storage__action-icon"]}
                          onClick={row.onEdit}
                          aria-label="Edit Storage Limit"
                          role="button"
                          tabIndex={0}
                        />
                      )}
                    </div>
                  );
                }
                return row[key] || "--";
              }}
            />
          )}
        </div>

        {/* PAGINATION */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setLimit(value);
            setPage(1);
          }}
        />
      </div>

      {/* VIEW MODAL */}
      <ViewStorageModal
        open={viewModal.open}
        orgId={viewModal.id}
        onClose={() => setViewModal({ open: false, id: null })}
        onError={(message) => showNotification(message, "error")}
      />

      {/* EDIT MODAL */}
      <EditStorageModal
        open={editModal.open}
        orgId={editModal.id}
        onClose={() => setEditModal({ open: false, id: null })}
        onSuccess={(message) => {
          showNotification(message || "Storage limit updated", "success");
          fetchStorageList();
        }}
        onError={(message) => showNotification(message, "error")}
      />
    </div>
  );
};

export default AdminStorage;
